import React, { useMemo } from "react";

import classNames from "classnames";

import { AccountMenu } from "../AccountMenu";
import { AppMessageBar } from "../AppMessageBar";
import { useAppSideNavOpenState } from "../AppSideNav/providers/AppSideNavOpenStateProvider";
import { OrganizationMenu } from "../OrganizationMenu";
import { PageHeader } from "../PageHeader";

import { useAppTopNavSlotContainerSetters } from "./AppTopNavSlot";
import { DeletedVersionBand } from "./DeletedVersionBand";

import styles from "./index.module.css";

import { getAlertOperationMessage } from "@/domain/Version";
import { useAppCurrentVersion } from "../AppProvider/AppCurrentVersionProvider";
import { useAppOrgsAndInvitedOrgs } from "../AppProvider/AppOrgsAndInvitedOrgsProvider";

/**
 * @description 画面上部に置くコンポーネント。
 * AppBaseLayout 直下で 1 回だけ render される。各ページから描画したい内容は
 * `<AppTopNavSlot>` を経由して push する。
 * showOrganizationMenu 時はヘッダー左に組織メニュー (組織設定などの動線) を出す
 * (サイドナビ非表示ページ・ヘッダーのみ表示モード)。
 * showSessionFacepile=false のときは在席 facepile の描画先を用意しない
 * (セッション書き込み自体は継続する。ヘッダーのみ表示モードで在席表示を消す用途)。
 */
export const AppTopNav: React.FC<{
  showOrganizationMenu?: boolean;
  showSessionFacepile?: boolean;
}> = ({ showOrganizationMenu = false, showSessionFacepile = true }) => {
  const { setChildrenContainer, setFacepileContainer } =
    useAppTopNavSlotContainerSetters();
  const { version } = useAppCurrentVersion();
  const { orgServiceType } = useAppOrgsAndInvitedOrgs();
  const { isToggleable } = useAppSideNavOpenState();

  const alertMessage = useMemo(
    () => getAlertOperationMessage(version, orgServiceType),
    [version, orgServiceType]
  );

  return (
    <>
      <div className={styles.wrapper}>
        {showOrganizationMenu && <OrganizationMenu />}
        <PageHeader />
        <div
          ref={setChildrenContainer}
          className={classNames(styles.children, {
            [styles.childrenWithoutMenuBtn]: !isToggleable,
          })}
        />
        <div className={styles.subcontent}>
          <div className={styles.menuNode}>
            {showSessionFacepile && (
              <div ref={setFacepileContainer} style={{ display: "contents" }} />
            )}
            <AccountMenu />
          </div>
        </div>
      </div>
      {alertMessage && (
        <div className={styles.printHidden}>
          <AppMessageBar intent="warning">{alertMessage}</AppMessageBar>
        </div>
      )}
      {version?.isDisabled && (
        <div className={styles.printHidden}>
          <DeletedVersionBand version={version} />
        </div>
      )}
    </>
  );
};

export { AppTopNavSlot, AppTopNavSlotProvider } from "./AppTopNavSlot";
